<template>
  <v-dialog
    :model-value="modelValue"
    max-width="520"
    scrim="rgba(0,0,0,0.65)"
    @update:modelValue="$emit('update:modelValue', $event)"
  >
    <v-card class="rounded-lg">
      <v-card-title class="py-3 d-flex align-center">
        <v-icon class="mr-2">mdi-format-font</v-icon>
        Transcription preview settings (applies to all lines)
        <v-spacer></v-spacer>
        <v-btn
          icon
          variant="text"
          @click="$emit('update:modelValue', false)"
          title="Close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-select
          :model-value="fontId"
          @update:modelValue="$emit('update:fontId', $event)"
          :items="fontOptions"
          item-title="text"
          item-value="value"
          label="Glyph font"
          density="compact"
          variant="outlined"
          hide-details
          class="mb-4"
          :disabled="!fonts.length"
        />

        <v-divider class="mb-4"></v-divider>

        <div class="text-caption mb-2">
          No font yet, or want to refresh it? Generate one from a keyboard's
          key images:
        </div>
        <div class="d-flex align-center flex-wrap generate-row mb-4">
          <v-select
            v-model="selectedKeyboard"
            :items="keyboardOptions"
            item-title="text"
            item-value="value"
            label="Keyboard"
            density="compact"
            variant="outlined"
            hide-details
            style="max-width: 240px"
            :disabled="!keyboardOptions.length"
          />
          <v-btn
            color="primary"
            :loading="generating"
            :disabled="!selectedKeyboard"
            @click="generate"
          >
            <v-icon start>mdi-auto-fix</v-icon>
            Generate
          </v-btn>
        </div>

        <v-switch
          :model-value="showFade"
          @update:modelValue="$emit('update:showFade', $event)"
          label="Fade the original line image in underneath the glyphs"
          color="primary"
          density="compact"
          hide-details
        />

        <v-switch
          :model-value="showMask"
          @update:modelValue="$emit('update:showMask', $event)"
          label="Clip that underlay to the line mask (polygon), not a rectangle"
          color="primary"
          density="compact"
          hide-details
          class="mt-1"
          :disabled="!showFade"
        />

        <v-alert
          v-if="error"
          type="warning"
          density="compact"
          class="mt-4"
          closable
          @click:close="$emit('clear-error')"
        >
          {{ error }}
        </v-alert>
        <v-alert
          v-else-if="selectedFont"
          type="info"
          variant="tonal"
          density="compact"
          class="mt-4"
        >
          "{{ selectedFont.name }}" :
          {{ selectedFont.glyph_chars.length }} key glyphs traced. Characters
          without a key image fall back to a regular font.
        </v-alert>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "LineGlyphPreview",
  props: {
    modelValue: { type: Boolean, default: false },
    fonts: { type: Array, default: () => [] },
    keyboards: { type: Array, default: () => [] },
    fontId: { type: [Number, String], default: null },
    showFade: { type: Boolean, default: true },
    showMask: { type: Boolean, default: true },
    generating: { type: Boolean, default: false },
    error: { type: String, default: "" },
  },
  emits: [
    "update:modelValue",
    "update:fontId",
    "update:showFade",
    "update:showMask",
    "generate",
    "clear-error",
  ],
  data() {
    return {
      selectedKeyboard: null, // "shared:3" | "personal:5"
    };
  },
  computed: {
    fontOptions() {
      return this.fonts.map((f) => ({
        text: `${f.name} (${f.keyboard_type})`,
        value: f.id,
      }));
    },
    keyboardOptions() {
      return this.keyboards.map((kb) => ({
        text: `${kb.name} (${kb.type})`,
        value: `${kb.type}:${kb.id}`,
      }));
    },
    selectedFont() {
      return this.fonts.find((f) => f.id === this.fontId) || null;
    },
  },
  methods: {
    generate() {
      if (!this.selectedKeyboard) return;
      const [keyboardType, keyboardId] = this.selectedKeyboard.split(":");
      this.$emit("generate", {
        keyboardType,
        keyboardId: Number(keyboardId),
      });
    },
  },
};
</script>

<style scoped>
.generate-row {
  gap: 12px;
}
</style>
